/**
 * Dev Login Utility
 * Provides a mock authentication bypass for local development without Supabase.
 * Only active when VITE_DEV_LOGIN=true.
 *
 * Usage: Set VITE_DEV_LOGIN=true in your .env to enable the dev login button
 * on the login page. Clicking it will create a fake session in localStorage
 * and redirect to the admin dashboard.
 *
 * @security — PRODUCTION SAFETY BOUNDARY
 *
 * This module CANNOT be activated in production. Three independent layers prevent it:
 *
 * 1. **Runtime guard**: `isDevLoginEnabled()` and `getDevSession()` both check
 *    `import.meta.env.VITE_DEV_LOGIN === 'true'`, which is statically replaced
 *    by Vite at build time and will never be `'true'` in a production build.
 *
 * 2. **Build-time guard**: `vite.config.ts` throws an Error if
 *    `mode === 'production' && process.env.VITE_DEV_LOGIN === 'true'`,
 *    causing `npm run build` / `vite build` to fail immediately.
 *
 * 3. **Dead-code elimination**: Vite replaces `import.meta.env.VITE_DEV_LOGIN`
 *    with a static value during production builds. Since it will never equal
 *    `'true'`, all guarded code paths are tree-shaken from the production bundle.
 *
 * Callers: LoginPage.tsx, PlatformLoginPage.tsx (both gate UI on `isDevLoginEnabled()`).
 */

export interface DevUser {
  id: string
  email: string
  role: 'admin' | 'platform_admin'
  name: string
}

const DEV_USERS: DevUser[] = [
  {
    id: 'dev-admin-001',
    email: 'dev@phyre.local',
    role: 'admin',
    name: 'Dev Admin',
  },
  {
    id: 'dev-platform-001',
    email: 'platform@phyre.local',
    role: 'platform_admin',
    name: 'Platform Admin',
  },
]

const DEV_SESSION_KEY = 'phyre_dev_session'

/**
 * Check whether dev-login bypass is enabled.
 *
 * @returns `true` only when `VITE_DEV_LOGIN=true` (statically replaced at build time)
 */
export function isDevLoginEnabled(): boolean {
  // Double-safety: never allow in production builds regardless of env var
  if (import.meta.env.PROD) return false
  return import.meta.env.VITE_DEV_LOGIN === 'true'
}

/**
 * Sign in with a mock dev user and persist the session to localStorage.
 *
 * @param userIndex - Index into the `DEV_USERS` array (default: 0 = admin)
 * @returns The mock {@link DevUser} that was signed in
 */
export function devSignIn(userIndex = 0): DevUser {
  const user = DEV_USERS[userIndex] ?? DEV_USERS[0]
  const session = {
    user,
    access_token: 'dev-token-' + Date.now(),
    expires_at: Date.now() + 24 * 60 * 60 * 1000, // 24h
  }
  localStorage.setItem(DEV_SESSION_KEY, JSON.stringify(session))
  return user
}

/** Remove the dev session from localStorage. */
export function devSignOut(): void {
  localStorage.removeItem(DEV_SESSION_KEY)
}

/**
 * Retrieve the current dev session from localStorage.
 * Returns `null` if dev login is disabled, the session is missing, or it has expired.
 *
 * @returns The dev session with user, token, and expiry — or `null`
 */
export function getDevSession(): { user: DevUser; access_token: string; expires_at: number } | null {
  // Double-safety: never allow in production builds regardless of env var
  if (import.meta.env.PROD) return null
  if (import.meta.env.VITE_DEV_LOGIN !== 'true') return null
  try {
    const raw = localStorage.getItem(DEV_SESSION_KEY)
    if (!raw) return null
    const session = JSON.parse(raw)
    if (session.expires_at < Date.now()) {
      localStorage.removeItem(DEV_SESSION_KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

export { DEV_USERS }
